import apiClient from '../../../../apiClient';

class TeamflectDataService {
  // ==================
  // Dashboard
  // ==================
  async getDashboardData(filters = {}) {
    try {
      const response = await apiClient.get('/peoplepulse/dashboard', { params: filters });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  // ==================
  // Goals & OKRs
  // ==================
  async getGoals(filters = {}) {
    try {
      const response = await apiClient.get('/peoplepulse/goals', { params: filters });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching goals:', error);
      return [];
    }
  }

  async createGoal(goalData) {
    try {
      const response = await apiClient.post('/peoplepulse/goals', goalData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating goal:', error);
      throw error;
    }
  }

  async updateGoal(goalId, goalData) {
    try {
      const response = await apiClient.put(`/peoplepulse/goals/${goalId}`, goalData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating goal:', error);
      throw error;
    }
  }

  async deleteGoal(goalId) {
    try {
      const response = await apiClient.delete(`/peoplepulse/goals/${goalId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting goal:', error);
      throw error;
    }
  }

  async updateGoalProgress(goalId, progress) {
    try {
      const response = await apiClient.patch(`/peoplepulse/goals/${goalId}/progress`, { progress });
      return response.data.data;
    } catch (error) {
      console.error('Error updating goal progress:', error);
      throw error;
    }
  }

  // ==================
  // 1-on-1 Meetings
  // ==================
  async getOneOnOneMeetings(filters = {}) {
    try {
      const response = await apiClient.get('/peoplepulse/one-on-one', { params: filters });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching 1-on-1 meetings:', error);
      return [];
    }
  }

  // Alias for getOneOnOneMeetings
  async getOneOnOnes(filters = {}) {
    return this.getOneOnOneMeetings(filters);
  }

  async getOneOnOneById(meetingId) {
    try {
      const response = await apiClient.get(`/peoplepulse/one-on-one/${meetingId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching 1-on-1 meeting:', error);
      throw error;
    }
  }

  async createOneOnOne(meetingData) {
    try {
      const response = await apiClient.post('/peoplepulse/one-on-one', meetingData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating 1-on-1:', error);
      throw error;
    }
  }

  async updateOneOnOne(meetingId, meetingData) {
    try {
      const response = await apiClient.put(`/peoplepulse/one-on-one/${meetingId}`, meetingData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating 1-on-1:', error);
      throw error;
    }
  }

  async getOneOnOneNotes(meetingId) {
    try {
      const response = await apiClient.get(`/peoplepulse/one-on-one/${meetingId}/notes`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching meeting notes:', error);
      return [];
    }
  }

  async addOneOnOneNote(meetingId, noteData) {
    try {
      const response = await apiClient.post(`/peoplepulse/one-on-one/${meetingId}/notes`, noteData);
      return response.data.data;
    } catch (error) {
      console.error('Error adding meeting note:', error);
      throw error;
    }
  }

  async addActionItem(meetingId, actionItemData) {
    try {
      const response = await apiClient.post(`/peoplepulse/one-on-one/${meetingId}/action-items`, actionItemData);
      return response.data.data;
    } catch (error) {
      console.error('Error adding action item:', error);
      throw error;
    }
  }

  // ==================
  // Performance Reviews
  // ==================
  async getReviewCycles(filters = {}) {
    try {
      const response = await apiClient.get('/peoplepulse/reviews/cycles', { params: filters });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching review cycles:', error);
      return [];
    }
  }

  async createReviewCycle(cycleData) {
    try {
      const response = await apiClient.post('/peoplepulse/reviews/cycles', cycleData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating review cycle:', error);
      throw error;
    }
  }

  async updateReviewCycle(cycleId, cycleData) {
    try {
      const response = await apiClient.put(`/peoplepulse/reviews/cycles/${cycleId}`, cycleData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating review cycle:', error);
      throw error;
    }
  }

  async getReviews(filters = {}) {
    try {
      const response = await apiClient.get('/peoplepulse/reviews', { params: filters });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching reviews:', error);
      return [];
    }
  }

  // Alias for getReviews
  async getReviewSubmissions(filters = {}) {
    return this.getReviews(filters);
  }

  async submitReview(reviewData) {
    try {
      const response = await apiClient.post('/peoplepulse/reviews', reviewData);
      return response.data.data;
    } catch (error) {
      console.error('Error submitting review:', error);
      throw error;
    }
  }

  async getReviewTemplates() {
    try {
      const response = await apiClient.get('/peoplepulse/reviews/templates');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching review templates:', error);
      return [];
    }
  }

  async createReviewTemplate(templateData) {
    try {
      const response = await apiClient.post('/peoplepulse/reviews/templates', templateData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating review template:', error);
      throw error;
    }
  }

  // ==================
  // Feedback & Recognition
  // ==================
  async getFeedback(filters = {}) {
    try {
      const response = await apiClient.get('/peoplepulse/feedback', { params: filters });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching feedback:', error);
      return [];
    }
  }

  // Alias for getFeedback
  async getFeedbackRequests(filters = {}) {
    return this.getFeedback(filters);
  }

  async giveFeedback(feedbackData) {
    try {
      const response = await apiClient.post('/peoplepulse/feedback', feedbackData);
      return response.data.data;
    } catch (error) {
      console.error('Error giving feedback:', error);
      throw error;
    }
  }

  // Alias for giveFeedback
  async submitFeedback(feedbackData) {
    return this.giveFeedback(feedbackData);
  }

  async request360Feedback(requestData) {
    try {
      const response = await apiClient.post('/peoplepulse/feedback/360-request', requestData);
      return response.data.data;
    } catch (error) {
      console.error('Error requesting 360 feedback:', error);
      throw error;
    }
  }

  // Alias for request360Feedback
  async requestFeedback(requestData) {
    return this.request360Feedback(requestData);
  }

  async getRecognitions(filters = {}) {
    try {
      const response = await apiClient.get('/peoplepulse/recognitions', { params: filters });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching recognitions:', error);
      return [];
    }
  }

  async giveRecognition(recognitionData) {
    try {
      const response = await apiClient.post('/peoplepulse/recognitions', recognitionData);
      return response.data.data;
    } catch (error) {
      console.error('Error giving recognition:', error);
      throw error;
    }
  }

  // ==================
  // Tasks & Action Items
  // ==================
  async getTasks(filters = {}) {
    try {
      const response = await apiClient.get('/peoplepulse/tasks', { params: filters });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      return [];
    }
  }

  async createTask(taskData) {
    try {
      const response = await apiClient.post('/peoplepulse/tasks', taskData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  }

  async updateTask(taskId, taskData) {
    try {
      const response = await apiClient.put(`/peoplepulse/tasks/${taskId}`, taskData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  }

  async completeTask(taskId) {
    try {
      const response = await apiClient.patch(`/peoplepulse/tasks/${taskId}/complete`);
      return response.data.data;
    } catch (error) {
      console.error('Error completing task:', error);
      throw error;
    }
  }

  async deleteTask(taskId) {
    try {
      const response = await apiClient.delete(`/peoplepulse/tasks/${taskId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  }

  // ==================
  // Analytics & Reports
  // ==================
  async getPerformanceAnalytics(filters = {}) {
    try {
      const response = await apiClient.get('/peoplepulse/analytics/performance', { params: filters });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching performance analytics:', error);
      throw error;
    }
  }

  // Alias for getPerformanceAnalytics
  async getPerformanceMetrics(filters = {}) {
    return this.getPerformanceAnalytics(filters);
  }

  async getEngagementMetrics(filters = {}) {
    try {
      const response = await apiClient.get('/peoplepulse/analytics/engagement', { params: filters });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching engagement metrics:', error);
      throw error;
    }
  }

  async getGoalCompletionReport(filters = {}) {
    try {
      const response = await apiClient.get('/peoplepulse/analytics/goal-completion', { params: filters });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching goal completion report:', error);
      throw error;
    }
  }

  async getReports(filters = {}) {
    try {
      const response = await apiClient.get('/peoplepulse/analytics/reports', { params: filters });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching reports:', error);
      return [];
    }
  }

  async exportReport(reportType, format = 'pdf') {
    try {
      const response = await apiClient.get(`/peoplepulse/analytics/export/${reportType}`, {
        params: { format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting report:', error);
      throw error;
    }
  }

  // Alias for exportReport with different parameter structure
  async exportAnalytics(format, options = {}) {
    const { chartType = 'dashboard' } = options;
    return this.exportReport(chartType, format);
  }

  // ==================
  // Users & Teams
  // ==================
  async getTeamMembers() {
    try {
      const response = await apiClient.get('/peoplepulse/team-members');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching team members:', error);
      return [];
    }
  }

  async getManagers() {
    try {
      const response = await apiClient.get('/peoplepulse/managers');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching managers:', error);
      return [];
    }
  }

  // ==================
  // Settings
  // ==================
  async getSettings() {
    try {
      const response = await apiClient.get('/peoplepulse/settings');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  }

  async updateSettings(settingsData) {
    try {
      const response = await apiClient.put('/peoplepulse/settings', settingsData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  }
}

export const peoplePulseDataService = new TeamflectDataService();


